import React from 'react';
import { Tooltip, ToggleButton, Theme } from '@mui/material';
import { CallMerge } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { useSelector } from 'react-redux';
import { CTFModuleTypes, PDFTronTools, DifferenceTypeNames } from 'types';
import { getSelectedModule, getSelectedTool } from 'store';
import { getIsStepAndRepeat } from 'store/inspection/inspectionSelectors';
import { getDisplayedDifferences } from 'store/request/inspections/selectors';
import selectTool from 'utils/selectTool/selectTool';

const useStyles = makeStyles()((theme: Theme) => ({
  toggleButton: {
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    padding: 0,
    margin: theme.spacing(0.25),
    color: 'rgba(255,255,255,.7)',
    border: 'none',
    '&:hover': {
      color: 'rgba(255,255,255)',
      backgroundColor: 'rgba(255,255,255,.04)',
    },
    '&.Mui-selected': {
      color: '#ffffff',
      backgroundColor: '#ea3632', // Red when active
    },
    '&.Mui-selected:hover': {
      backgroundColor: '#ea3632',
    },
    '&.Mui-disabled': {
      color: 'rgba(255,255,255,.37) !important',
    },
  },
  icon: {
    fontSize: '16px',
  },
  disabled: {
    color: 'rgba(255,255,255,.37) !important',
  },
}));

interface MergeGraphicsButtonProps {
  disabled?: boolean;
}

const MergeGraphicsButton: React.FC<MergeGraphicsButtonProps> = ({ disabled = false }) => {
  const { classes } = useStyles();

  const selectedModule = useSelector(getSelectedModule);
  const selectedTool = useSelector(getSelectedTool);
  const isStepAndRepeat = useSelector(getIsStepAndRepeat);
  const displayedDifferences = useSelector(getDisplayedDifferences);

  // Only show button in Graphics tab, not in All tab
  const isGraphicsTab = selectedModule === CTFModuleTypes.graphics;

  if (!isGraphicsTab) {
    return null;
  }

  const isMergeToolSelected = selectedTool === PDFTronTools.MERGE_GRAPHICS_ZONE;

  // Count available graphics differences (including merged ones since they can be merged again)
  const graphicsDifferences = displayedDifferences.filter((diff) => diff.type === DifferenceTypeNames.Graphics);
  const hasEnoughDifferences = graphicsDifferences.length >= 2;

  // Disable merge tool when repeats are detected or insufficient differences
  const isDisabled = disabled || isStepAndRepeat || !hasEnoughDifferences;

  const handleToggle = () => {
    if (isMergeToolSelected) {
      // If merge tool is selected, switch to pan tool
      selectTool(PDFTronTools.PAN);
    } else {
      // If merge tool is not selected, switch to merge tool
      selectTool(PDFTronTools.MERGE_GRAPHICS_ZONE);
    }
  };

  // Determine tooltip text based on disabled state
  const getTooltipText = () => {
    if (isStepAndRepeat) {
      return 'Merge tool is not available for step-and-repeat inspections';
    }
    if (!hasEnoughDifferences) {
      return `Merge tool requires at least 2 graphics differences (${graphicsDifferences.length} available, including merged ones)`;
    }
    return 'Merge nearby graphics differences';
  };

  const button = (
    <ToggleButton
      value="mergeGraphicsButton"
      selected={isMergeToolSelected}
      className={classes.toggleButton}
      onClick={handleToggle}
      disabled={isDisabled}
      classes={{ disabled: classes.disabled }}
      disableRipple
      data-testid="merge-graphics-button"
    >
      <CallMerge className={classes.icon} />
    </ToggleButton>
  );

  return (
    <Tooltip title={getTooltipText()} placement="bottom">
      {isDisabled ? <span>{button}</span> : button}
    </Tooltip>
  );
};

export default MergeGraphicsButton;
